/**
 * Form primitives.
 *
 * Two things every field here does that plain inputs do not:
 *
 *   - Labels are real <label> elements bound by id, so screen readers and
 *     click-to-focus both work.
 *   - Tri-state yes/no keeps "not answered" distinct from "no". On an
 *     application form those are different answers, and a control that cannot
 *     express the difference forces a guess.
 */

import { useId, useState, type ReactNode } from "react";

export function Field({
  label, hint, children, error,
}: { label: string; hint?: ReactNode; children: (id: string) => ReactNode; error?: string }) {
  const id = useId();
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>{label}</label>
      {hint && <p className="muted small field-hint">{hint}</p>}
      {children(id)}
      {error && <p className="err small field-hint">{error}</p>}
    </div>
  );
}

/**
 * A destructive "Remove" that takes a second, deliberate press: the first click
 * asks, only "Remove for good" deletes. The same idiom Resume and Tracks use,
 * shared so every delete in the app is guarded the same way.
 */
export function ConfirmRemove({ onRemove, busy, label = "Remove", className = "step-link small" }: {
  onRemove: () => void;
  busy?: boolean;
  label?: string;
  className?: string;
}) {
  const [confirming, setConfirming] = useState(false);
  if (confirming) {
    return (
      <span className="remove-confirm">
        <button type="button" className={`${className} err`} disabled={busy} onClick={() => { setConfirming(false); onRemove(); }}>Remove for good</button>
        <button type="button" className={className} disabled={busy} onClick={() => setConfirming(false)}>Keep</button>
      </span>
    );
  }
  return (
    <button type="button" className={className} disabled={busy} onClick={() => setConfirming(true)}>{label}</button>
  );
}

export function TextField({
  label, hint, value, onChange, placeholder, type = "text", suggestions,
}: {
  label: string; hint?: ReactNode; value: string | null;
  onChange: (v: string) => void; placeholder?: string; type?: string;
  /** Offered as the box is typed in (a datalist): [value, shown name]. Free text is still taken. */
  suggestions?: ReadonlyArray<readonly [string, string]>;
}) {
  return (
    <Field label={label} hint={hint}>
      {(id) => (
        <>
          <input
            id={id} className="input" type={type} value={value ?? ""}
            placeholder={placeholder}
            list={suggestions ? `${id}-list` : undefined}
            onChange={(e) => onChange(e.target.value)}
          />
          {suggestions && (
            <datalist id={`${id}-list`}>
              {suggestions.map(([v, name]) => <option key={v} value={v}>{name}</option>)}
            </datalist>
          )}
        </>
      )}
    </Field>
  );
}

/**
 * Chip input. Comma or Enter commits an entry; Backspace on an empty box
 * removes the last one. Values are kept in the order typed, because these
 * lists are read back to the user as their own words.
 */
export function TagField({
  label, hint, values, onChange, placeholder,
}: {
  label: string; hint?: ReactNode; values: string[];
  onChange: (v: string[]) => void; placeholder?: string;
}) {
  const [draft, setDraft] = useState("");

  const commit = (raw: string) => {
    const t = raw.trim().replace(/,$/, "").trim();
    if (t === "") return;
    if (values.some((v) => v.toLowerCase() === t.toLowerCase())) { setDraft(""); return; }
    onChange([...values, t]);
    setDraft("");
  };

  return (
    <Field label={label} hint={hint}>
      {(id) => (
        <div className="tagbox">
          {values.map((v, i) => (
            <span className="tag" key={`${v}-${i}`}>
              {v}
              <button
                type="button" className="tag-x" aria-label={`Remove ${v}`}
                onClick={() => onChange(values.filter((_, j) => j !== i))}
              >×</button>
            </span>
          ))}
          <input
            id={id} className="tag-input" value={draft}
            placeholder={values.length === 0 ? placeholder : ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v.includes(",")) commit(v);
              else setDraft(v);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(draft); }
              if (e.key === "Backspace" && draft === "" && values.length > 0) {
                onChange(values.slice(0, -1));
              }
            }}
            onBlur={() => commit(draft)}
          />
        </div>
      )}
    </Field>
  );
}

export function CheckGroup({
  label, hint, options, values, onChange,
}: {
  label: string; hint?: ReactNode;
  options: ReadonlyArray<{ value: string; label: string }>;
  values: string[]; onChange: (v: string[]) => void;
}) {
  return (
    <fieldset className="field fieldset">
      <legend className="field-label">{label}</legend>
      {hint && <p className="muted small field-hint">{hint}</p>}
      <div className="choices">
        {options.map((o) => (
          <label key={o.value} className="choice">
            <input
              type="checkbox" checked={values.includes(o.value)}
              onChange={(e) =>
                onChange(e.target.checked
                  ? [...values, o.value]
                  : values.filter((v) => v !== o.value))
              }
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function RadioGroup<T extends string>({
  label, hint, options, value, onChange,
}: {
  label: string; hint?: ReactNode;
  options: ReadonlyArray<{ value: T; label: string; note?: string }>;
  value: T | null; onChange: (v: T) => void;
}) {
  const name = useId();
  return (
    <fieldset className="field fieldset">
      <legend className="field-label">{label}</legend>
      {hint && <p className="muted small field-hint">{hint}</p>}
      <div className="choices stack">
        {options.map((o) => (
          <label key={o.value} className="choice">
            <input
              type="radio" name={name} checked={value === o.value}
              onChange={() => onChange(o.value)}
            />
            <span>
              {o.label}
              {o.note && <span className="muted small choice-note">{o.note}</span>}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Yes / No / Not answered.
 *
 * The third option is not padding. NULL on these columns means the user has
 * not answered, and automation is required to select decline-to-answer rather
 * than infer one. A two-state control would silently turn every unanswered
 * question into "no".
 */
export function YesNo({
  label, hint, value, onChange, allowUnanswered = true,
}: {
  label: string; hint?: ReactNode; value: boolean | null;
  onChange: (v: boolean | null) => void; allowUnanswered?: boolean;
}) {
  const name = useId();
  const opts: Array<{ key: string; v: boolean | null; label: string }> = [
    { key: "y", v: true, label: "Yes" },
    { key: "n", v: false, label: "No" },
  ];
  if (allowUnanswered) opts.push({ key: "u", v: null, label: "Not answered" });

  return (
    <fieldset className="field fieldset">
      <legend className="field-label">{label}</legend>
      {hint && <p className="muted small field-hint">{hint}</p>}
      <div className="choices">
        {opts.map((o) => (
          <label key={o.key} className="choice">
            <input
              type="radio" name={name} checked={value === o.v}
              onChange={() => onChange(o.v)}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Dollars in the box, cents in the database. */
export function MoneyField({
  label, hint, cents, onChange,
}: { label: string; hint?: ReactNode; cents: number | null; onChange: (c: number | null) => void }) {
  const [text, setText] = useState(cents === null ? "" : String(Math.round(cents / 100)));
  return (
    <Field label={label} hint={hint}>
      {(id) => (
        <div className="prefixed">
          <span className="prefix" aria-hidden="true">$</span>
          <input
            id={id} className="input" inputMode="numeric" value={text}
            placeholder="120000"
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9]/g, "");
              setText(raw);
              onChange(raw === "" ? null : Number(raw) * 100);
            }}
          />
        </div>
      )}
    </Field>
  );
}

export function ErrorList({ errors }: { errors: string[] }) {
  if (errors.length === 0) return null;
  return (
    <ul className="errlist">
      {errors.map((e, i) => <li key={i} className="err small">{e}</li>)}
    </ul>
  );
}
